import { useMemo, useState } from "react";
import { AlertTriangle, Package, Search, ShoppingBag, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl, dataBR, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, Field, Surface, Thumb } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";

const norm = (v) =>
  (v || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export function PartnerStock({ stock, movements, onReload }) {
  const [detail, setDetail] = useState(null);
  const [selling, setSelling] = useState(null);
  const [term, setTerm] = useState("");
  const [category, setCategory] = useState("");

  // "Meu estoque" é saldo atual: item zerado sai da tela (o histórico continua intacto).
  const owned = useMemo(() => stock.filter((c) => Number(c.quantity) > 0), [stock]);

  const categories = useMemo(
    () => [...new Set(owned.map((c) => c.category).filter(Boolean))].sort(),
    [owned]
  );

  const list = useMemo(() => {
    const q = norm(term).trim();
    return owned.filter((c) => {
      if (category && c.category !== category) return false;
      if (!q) return true;
      return [c.name, c.code, c.variation_name, c.category].some((f) => norm(f).includes(q));
    });
  }, [owned, term, category]);

  if (owned.length === 0) {
    return (
      <EmptyState
        icon={Package}
        title="Seu estoque está vazio"
        description="Os produtos que você receber serão exibidos aqui. Peça uma reposição para começar."
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="partner-stock-list">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Buscar produto, SKU ou combinação..."
            className="pl-9"
            data-testid="partner-stock-search"
          />
        </div>
        {categories.length > 1 && (
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="partner-stock-category-filter"
            className="h-10 rounded-md border border-white/10 bg-secondary/60 px-3 text-sm outline-none sm:w-52"
          >
            <option value="">Todas as categorias</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        )}
      </div>

      <p className="text-[11px] text-muted-foreground" data-testid="partner-stock-count">
        {list.length} de {owned.length} {owned.length === 1 ? "item" : "itens"} com você
      </p>

      {list.length === 0 ? (
        <EmptyState
          icon={Search}
          title="Nada encontrado"
          description="Ajuste a busca ou o filtro de categoria."
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {list.map((c) => (
            <Surface
              key={c.key || c.product_id}
              className="flex min-w-0 flex-col overflow-hidden p-3"
              data-testid={`partner-product-card-${c.key || c.product_id}`}
            >
              <div className="flex items-start gap-3">
                <Thumb src={fileUrl(c.photo_path)} alt={c.name} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{c.name}</p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1">
                    {c.kind === "KIT" && (
                      <span
                        className="rounded-full border border-primary/40 px-1.5 text-[10px] uppercase text-brand"
                        data-testid={`partner-stock-kit-badge-${c.key || c.product_id}`}
                      >
                        kit
                      </span>
                    )}
                    {c.variation_name && (
                      <span
                        className="truncate rounded-full border border-white/10 px-1.5 text-[10px] text-muted-foreground"
                        data-testid={`partner-stock-variation-${c.key || c.product_id}`}
                      >
                        {c.variation_name}
                      </span>
                    )}
                  </div>
                  <p
                    className="mt-1 truncate font-num text-[11px] text-muted-foreground"
                    data-testid={`partner-stock-sku-${c.key || c.product_id}`}
                  >
                    {c.code || "sem código"}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p
                    className="font-num text-xl leading-none"
                    data-testid={`partner-qty-${c.key || c.product_id}`}
                  >
                    {Number(c.quantity).toLocaleString("pt-BR")}
                  </p>
                  <p className="mt-1 text-[10px] text-muted-foreground">comigo</p>
                </div>
              </div>

              <dl
                className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 border-t border-white/10 pt-3 text-[11px]"
                data-testid={`partner-stock-prices-${c.key || c.product_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-muted-foreground">Consignado</dt>
                  <dd className="font-num">
                    {c.price_missing ? (
                      <span className="text-amber-300">—</span>
                    ) : (
                      brl(c.consign_price)
                    )}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-muted-foreground">Catálogo</dt>
                  <dd className="font-num">
                    {c.catalog_missing ? (
                      <span className="text-amber-300">—</span>
                    ) : (
                      brl(c.catalog_price)
                    )}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-muted-foreground">Valor em estoque</dt>
                  <dd className="font-num">
                    {c.stock_value === null ? "—" : brl(c.stock_value)}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-muted-foreground">Potencial</dt>
                  <dd className="font-num text-emerald-300">
                    {c.sales_potential === null ? "—" : brl(c.sales_potential)}
                  </dd>
                </div>
              </dl>

              {(c.price_missing || c.catalog_missing) && (
                <p
                  className="mt-2 text-[11px] text-amber-300"
                  data-testid={`partner-stock-price-warning-${c.key || c.product_id}`}
                >
                  {c.price_missing && "Preço de consignação não definido. "}
                  {c.catalog_missing && "Preço do catálogo não disponível (item fora do catálogo)."}
                </p>
              )}

              {c.stalled && (
                <p
                  className={`mt-3 flex items-start gap-2 rounded-lg border p-2 text-[11px] ${
                    c.needs_return_review
                      ? "border-red-500/40 bg-red-500/10 text-red-200"
                      : "border-amber-500/30 bg-amber-500/10 text-amber-200"
                  }`}
                  data-testid={`partner-stalled-${c.key || c.product_id}`}
                >
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {c.needs_return_review
                    ? `Estoque parado há ${c.days_idle} dias — considere avaliar devolução.`
                    : `Sem venda há ${c.days_idle} dias.`}
                </p>
              )}

              <div className="mt-3 flex flex-wrap gap-2 border-t border-white/10 pt-3">
                <Button
                  size="sm"
                  onClick={() => setSelling(c)}
                  data-testid={`register-sale-btn-${c.key || c.product_id}`}
                  className="h-9 flex-1 gap-1.5 rounded-full text-xs font-semibold"
                >
                  <ShoppingBag className="h-3.5 w-3.5" /> Registrar venda
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDetail(c)}
                  data-testid={`product-detail-btn-${c.key || c.product_id}`}
                  className="h-9 rounded-full text-xs"
                >
                  Ver detalhes
                </Button>
              </div>
            </Surface>
          ))}
        </div>
      )}

      {detail && (
        <ProductDetailModal
          item={detail}
          movements={movements.filter(
            (m) =>
              m.product_id === detail.product_id &&
              (m.variation_id || null) === (detail.variation_id || null)
          )}
          onClose={() => setDetail(null)}
          onReload={onReload}
        />
      )}
      {selling && (
        <SaleModal item={selling} onClose={() => setSelling(null)} onSaved={onReload} />
      )}
    </div>
  );
}

function ProductDetailModal({ item, movements, onClose, onReload }) {
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);

  const reverse = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/partner/sales/${confirming.sale_id}/reverse`);
      toast.success(`Venda estornada: ${data.reversed_units} un devolvida(s) ao seu estoque.`);
      setConfirming(null);
      onClose();
      onReload();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title={item.name}
      description={`${item.code || "sem código"} · ${
        item.price_missing
          ? "preço de consignação não definido"
          : `${brl(item.consign_price)} por unidade`
      }`}
      testid="partner-product-detail-modal"
    >
      <div className="mt-2 space-y-4">
        <div className="flex items-center gap-4">
          <Thumb src={fileUrl(item.photo_path)} alt={item.name} className="h-20 w-20" />
          <div>
            <p className="font-num text-2xl">{Number(item.quantity).toLocaleString("pt-BR")} un</p>
            <p className="text-xs text-muted-foreground">comigo agora</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Consignado: {item.price_missing ? "não definido" : brl(item.consign_price)} ·
              Catálogo: {item.catalog_missing ? "não disponível" : brl(item.catalog_price)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Última venda: {item.last_sale_at ? dataBR(item.last_sale_at) : "sem venda ainda"}
            </p>
          </div>
        </div>
        <div>
          <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
            Movimentações
          </p>
          {movements.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma movimentação ainda.</p>
          ) : (
            <ul className="max-h-64 divide-y divide-white/10 overflow-y-auto">
              {movements.map((m) => (
                <li key={m.id} className="flex flex-wrap items-center gap-2 py-2.5 text-sm">
                  <span className="w-14 shrink-0 font-num text-xs text-muted-foreground">
                    {dataBR(m.created_at)}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-xs">
                    {m.type_label}
                    {m.reversed_at && (
                      <span className="ml-1 text-[10px] text-amber-300">
                        (estornada em {dataBR(m.reversed_at)}
                        {m.reversed_by ? ` · ${m.reversed_by}` : ""})
                      </span>
                    )}
                  </span>
                  <span
                    className={`shrink-0 font-num text-sm ${m.quantity >= 0 ? "text-emerald-300" : "text-red-300"}`}
                  >
                    {m.quantity >= 0 ? "+" : "−"}
                    {Math.abs(m.quantity).toLocaleString("pt-BR")} un
                  </span>
                  {m.can_reverse && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setConfirming(m)}
                      data-testid={`undo-sale-btn-${m.id}`}
                      className="h-7 gap-1 rounded-full px-2 text-[11px]"
                    >
                      <Undo2 className="h-3 w-3" /> Desfazer venda
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {confirming && (
          <div
            className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3"
            data-testid="undo-sale-confirm-box"
          >
            <p className="text-sm font-medium text-amber-100">Desfazer esta venda?</p>
            <p className="mt-1 text-xs text-amber-200/80">
              Esta ação devolverá a unidade ao estoque do parceiro.
            </p>
            <div className="mt-3 flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirming(null)}
                className="rounded-full"
                data-testid="undo-sale-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={reverse}
                disabled={busy}
                className="rounded-full"
                data-testid="undo-sale-confirm-btn"
              >
                {busy ? "Desfazendo..." : "Desfazer venda"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </ResponsiveModal>
  );
}

function SaleModal({ item, onClose, onSaved }) {
  const [qty, setQty] = useState("1");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const quantity = Math.max(0, Math.min(Number(item.quantity), Number(qty || 0)));

  const submit = async (e) => {
    e.preventDefault();
    if (Number(qty || 0) > Number(item.quantity)) {
      toast.error(
        `Você tem ${Number(item.quantity).toLocaleString("pt-BR")} un desta combinação com você.`
      );
      return;
    }
    if (quantity <= 0) {
      toast.error("Informe a quantidade vendida.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/partner/sales", {
        items: [
          { product_id: item.product_id, variation_id: item.variation_id || null, quantity },
        ],
        notes,
      });
      toast.success(`Venda registrada: ${data.units} un · ${brl(data.total_value)}.`);
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Registrar venda"
      description={`${item.name} · você tem ${item.quantity} un`}
      testid="partner-sale-modal"
    >
      <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
        <Field label="Quantidade vendida">
          <Input
            type="number"
            min="1"
            max={item.quantity}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            className="font-num"
            data-testid="sale-qty-input"
          />
        </Field>
        <div className="rounded-lg border border-white/10 bg-secondary/40 p-3 text-sm">
          Valor da venda:{" "}
          <span className="font-num" data-testid="sale-total-value">
            {item.price_missing ? "preço não definido" : brl(quantity * item.consign_price)}
          </span>
        </div>
        <Field label="Observação">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            data-testid="sale-notes-input"
          />
        </Field>
        <div className="flex justify-end gap-2 border-t border-white/10 pt-4">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-full" data-testid="sale-cancel-btn">
            Cancelar
          </Button>
          <Button type="submit" disabled={busy} className="rounded-full" data-testid="sale-submit-btn">
            {busy ? "Salvando..." : "Confirmar venda"}
          </Button>
        </div>
      </form>
    </ResponsiveModal>
  );
}
